import { stockOptionsMpvIds } from './constants'
import { BFFPageData } from '../../services/serviceClients/BffClient'
import StockOptionData from './../../constants/StockOptionData.json'

export interface StockOptionDataType {
  [locale: string]: {
    sectionTitle: string
    sectionDescription: string
    highlightsSectionTitle: string
    optionsSectionTitle: string
    uploadDesignCta: string
    viewDetailsCta: string
    browseDesignCta: string
    outOfStockContent: string
    mpvIds: MpvIdsData
  }
}

export type MpvIdsData = {
  [mpvId: string]: MpvIdsContent
}

export type CalloutSkin =
  | 'standard'
  | 'info'
  | 'accent'
  | 'success'
  | 'warning'
  | 'error'
  | 'help'
  | 'promo'
  | 'holiday'
  | 'foil'
  | 'new'
  | 'announcement'
  | 'discount'

export type MpvIdsContent = {
  version?: number
  name?: string
  quantities?: { default: number; recommended?: number }
  coreProductId?: string
  merchandisingName: string
  premiumMerchandisingName?: string
  marketingText: string
  imageUrl: string
  highlights: string[]
  pdpLink?: string
  tabImageUrl?: string
  options: {
    [optionKey: string]: string
  }
  browseDesign?: {
    link: string
  }
  callout?: {
    skin: CalloutSkin
    text: string
  }
  disableUploadFlow?: boolean
}

export type StockOptionsMpvIds = {
  [locale: string]: string[]
}

export function buildStockOptionData(pageData: BFFPageData, locale: string) {
  const mpvData: MpvIdsData = {}
  const staticData = (StockOptionData as StockOptionDataType)[locale]
  const filteredMpvs = pageData?.mpvs?.filter(({ mpvId }) => (stockOptionsMpvIds as StockOptionsMpvIds)?.[locale]?.includes(mpvId))
  filteredMpvs?.forEach(({ mpvId, coreProductId, version, quantities, name }) => {
    const staticMpvData = staticData?.mpvIds?.[mpvId]

    const galleryUrl = pageData.galleries?.find((g) => g.productKey === coreProductId)?.url || staticMpvData?.browseDesign?.link || ''

    const pdpLink = pageData.pageUrls?.find((p) => p.mpvId === mpvId)?.url || staticMpvData?.pdpLink || ''

    mpvData[mpvId] = {
      coreProductId,
      version,
      quantities,
      name,
      ...staticMpvData,
      pdpLink,
      browseDesign: {
        link: galleryUrl,
      },
    }
  })

  return {
    ...staticData,
    mpvIds: sortStockOptionsData(mpvData, locale),
  }
}
function sortStockOptionsData(data: MpvIdsData, locale: string) {
  const referenceArray = (stockOptionsMpvIds as StockOptionsMpvIds)[locale]
  const indexMap = new Map(referenceArray?.map((item, index) => [item, index]))
  return Object.fromEntries(
    Object.entries(data).sort(([keyA], [keyB]) => (indexMap.get(keyA) ?? referenceArray.length) - (indexMap.get(keyB) ?? referenceArray.length)),
  )
}
export function isOutOfStock(mpvId: string, pageData: BFFPageData | undefined) {
  return pageData?.mpvs?.find((mpv) => mpv.mpvId === mpvId)?.pastaOutOfStock ?? false
}

export function reorderOutOfStockItems(pageData: BFFPageData | undefined, mpvIdsData: [string, MpvIdsData[keyof MpvIdsData]][]) {
  const outOfStockItems: MpvIdsData = {}

  for (const [mpvId, item] of mpvIdsData) {
    if (isOutOfStock(mpvId, pageData)) {
      outOfStockItems[mpvId] = item
    }
  }

  const reorderedData = {
    ...Object.fromEntries(mpvIdsData.filter(([key]) => !(key in outOfStockItems))),
    ...outOfStockItems,
  }

  return Object.entries(reorderedData)
}
